import { FirebaseDB } from '../firebase/config';
import { collection, addDoc, query, where, getDocs } from 'firebase/firestore/lite';
import { serverTimestamp } from 'firebase/firestore/lite';
import moment from 'moment';


// ─────────────────────────────────────────────────────────
// Helpers de semana ISO (lunes a domingo)
// ─────────────────────────────────────────────────────────

/**
 * Devuelve la clave de semana ISO para una fecha dada.
 * Formato: "YYYY-WXX"  (ej. "2026-W26")
 * @param {Date|moment.Moment} date
 * @returns {string}
 */
export const getSemanaISO = (date) => {
    return moment(date).isoWeekYear() + '-W' + String(moment(date).isoWeek()).padStart(2, '0');
};

/**
 * Devuelve el rango lunes-domingo de una semana ISO.
 * @param {string} semanaISO  ej. "2026-W26"
 * @returns {{ lunes: moment.Moment, domingo: moment.Moment }}
 */
export const getRangoSemana = (semanaISO) => {
    const [yearStr, weekStr] = semanaISO.split('-W');
    const year = parseInt(yearStr, 10);
    const week = parseInt(weekStr, 10);
    const lunes = moment().isoWeekYear(year).isoWeek(week).startOf('isoWeek');
    const domingo = moment().isoWeekYear(year).isoWeek(week).endOf('isoWeek');
    return { lunes, domingo };
};

/**
 * Formatea una semana ISO para mostrarla en UI.
 * Ej. "2026-W26" → "Lun 22 Jun – Dom 28 Jun 2026"
 * @param {string} semanaISO
 * @returns {string}
 */
export const formatSemanaLabel = (semanaISO) => {
    const { lunes, domingo } = getRangoSemana(semanaISO);
    return `${lunes.format('ddd D MMM')} – ${domingo.format('ddd D MMM YYYY')}`;
};

// ─────────────────────────────────────────────────────────
// Semana actual y navegación
// ─────────────────────────────────────────────────────────

/** Devuelve la clave ISO de la semana actual. */
export const getSemanaActual = () => getSemanaISO(new Date());

/**
 * Avanza o retrocede N semanas desde una semana ISO dada.
 * @param {string} semanaISO
 * @param {number} delta   +1 = siguiente, -1 = anterior
 * @returns {string}
 */
export const navegarSemana = (semanaISO, delta) => {
    const { lunes } = getRangoSemana(semanaISO);
    return getSemanaISO(lunes.clone().add(delta, 'weeks'));
};

// ─────────────────────────────────────────────────────────
// Cálculo de métricas sobre la colección de clientes
// ─────────────────────────────────────────────────────────

/**
 * Determina si un cliente entra en riesgo durante una semana específica.
 * Un cliente está "en riesgo" en la semana W si algún día D ∈ [lunes, domingo]
 * cumple: diasDesdeUltimoPedido(D) >= frecuenciaPedidos.
 *
 * @param {object} cliente   Documento de cliente de Firestore
 * @param {string} semanaISO
 * @returns {boolean}
 */
export const clienteEnRiesgoEnSemana = (cliente, semanaISO) => {
    const frec = parseInt(cliente.frecuenciaPedidos);
    if (!cliente.fechaUltimoPedido || isNaN(frec)) return false;

    const ultimoPedido = moment(cliente.fechaUltimoPedido, 'YYYY-MM-DD');
    if (!ultimoPedido.isValid()) return false;

    const { lunes, domingo } = getRangoSemana(semanaISO);

    // Días transcurridos desde el último pedido hasta el lunes de la semana
    const diasAlLunes = lunes.startOf('day').diff(ultimoPedido.startOf('day'), 'days');
    // Días transcurridos hasta el domingo
    const diasAlDomingo = domingo.startOf('day').diff(ultimoPedido.startOf('day'), 'days');

    // Si la frecuencia se cumple en algún punto del rango [lunes, domingo]
    return diasAlLunes <= frec && frec <= diasAlDomingo;
};

/**
 * Determina si un cliente fue contactado durante una semana específica.
 * Requiere que el cliente tenga `ultimoContacto.fecha`.
 *
 * @param {object} cliente
 * @param {string} semanaISO
 * @returns {boolean}
 */
export const clienteContactadoEnSemana = (cliente, semanaISO) => {
    if (!cliente.ultimoContacto?.fecha) return false;

    const { lunes, domingo } = getRangoSemana(semanaISO);

    let fechaContacto;
    const fc = cliente.ultimoContacto.fecha;
    if (fc?.seconds) {
        // Timestamp de Firestore
        fechaContacto = moment(fc.seconds * 1000);
    } else if (fc?.toDate) {
        fechaContacto = moment(fc.toDate());
    } else {
        fechaContacto = moment(fc);
    }

    if (!fechaContacto.isValid()) return false;

    return fechaContacto.isBetween(lunes.startOf('day'), domingo.endOf('day'), null, '[]');
};

/**
 * Calcula las métricas de riesgo/contacto para una semana dada.
 * @param {Array} clientes   Lista completa de clientes ya cargados
 * @param {string} semanaISO
 * @returns {{ enRiesgo: number, contactados: number, tasa: string }}
 */
export const calcularMetricasRiesgo = (clientes, semanaISO) => {
    const enRiesgoList = clientes.filter(c => clienteEnRiesgoEnSemana(c, semanaISO));
    const contactadosList = enRiesgoList.filter(c => clienteContactadoEnSemana(c, semanaISO));

    const enRiesgo = enRiesgoList.length;
    const contactados = contactadosList.length;
    const tasa = enRiesgo > 0 ? Math.round((contactados / enRiesgo) * 100) : 0;

    return { enRiesgo, contactados, tasa };
};

// ─────────────────────────────────────────────────────────
// Firestore: correoLogs
// ─────────────────────────────────────────────────────────

/**
 * Registra un evento de correo masivo enviado en la colección `correoLogs`.
 * @param {object} params
 * @param {string} params.usuarioId
 * @param {string} params.usuarioNombre
 * @param {string} params.giro
 * @param {number} params.cantidadDestinatarios
 * @returns {Promise<void>}
 */
export const logCorreoMasivo = async ({ usuarioId, usuarioNombre, giro, cantidadDestinatarios }) => {
    const semanaISO = getSemanaISO(new Date());
    await addDoc(collection(FirebaseDB, 'correoLogs'), {
        usuarioId,
        usuarioNombre,
        giro,
        cantidadDestinatarios,
        semanaISO,
        fechaHora: serverTimestamp(),
    });
};

/**
 * Obtiene todos los registros de `correoLogs` para una semana ISO.
 * @param {string} semanaISO
 * @returns {Promise<Array>}
 */
export const getCorreoLogsForSemana = async (semanaISO) => {
    const q = query(
        collection(FirebaseDB, 'correoLogs'),
        where('semanaISO', '==', semanaISO)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};
